import React, {useEffect, useRef, useState} from 'react';
import {useOrganizationService} from "../../../services";
import {useJwt} from "../../../helpers/common";
import {formatDateWithDay} from "../../../helpers/data-format";
import {useDispatch, useSelector} from 'react-redux';
import {HiOutlineDotsVertical, HiPlusCircle} from "react-icons/hi";
import {setshoworgScheduleEdit, setOrganizationOnboardSettings} from '../../../redux/slices/organization';
import DataTable from '../../../components/Tables/DataTable';
import CustomButton from '../../../components/common/CustomButton';
import SchedulesModal from './SchedulesModal';
import ConfirmDeleteModal from '../../../components/Modals/ConfirmDeleteModal';
import {
  OnboardingSpotlight,
  OnboardingStepKeyEnum,
  useOnboardingFlattenedTour,
  useOnboardingNavigator,
  useOnboardingRestart,
  useOnboardingRoadmap,
  useOnboardingStep
} from '../../../components/Onboarding';

const Schedule = () => {
  const dispatch = useDispatch()
  const [currentData,setCurrentData] = useState<any>({})
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const JWTdata:any = useJwt();
  const org_id = JWTdata.org_id;
  const { 
    orgSchedule=[],
    orgScheduleLoading=false,
    showOrgScheduleEdit,
    onboardSettings = {}
  } = useSelector((state: any) => state.organization);
  const { 
    deleteOrgSchedule,
    fetchOrgSchedule,
    updateOrganization
  } = useOrganizationService();
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'date',
    direction: 'asc'
  });
  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showDrawer, setShowDrawer] = useState(false);
  const addHolidayWrapperRef = useRef<HTMLDivElement>(null);
  const { isCurrent } = useOnboardingRoadmap();
  const { totalSteps: globalTotal, getIndexForStepKey } = useOnboardingFlattenedTour();
  const { goToNextFrom } = useOnboardingNavigator();
  const restartToken = useOnboardingRestart(OnboardingStepKeyEnum.OrgHolidays);
  const { 
    show: showHolidaySpotlight, 
    complete: completeHoliday 
  } = useOnboardingStep(OnboardingStepKeyEnum.OrgHolidays, { shouldShow: true});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const openId = openMenuId;
      if (openId && menuRefs.current[openId]) {
        if (
          menuRefs.current[openId] &&
          !menuRefs.current[openId]?.contains(event.target as Node)
        ) {
          setOpenMenuId(null);
        }
      }
    };
    if (openMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

    useEffect(()=>{
      if(org_id){
        fetchOrgSchedule(org_id)
        dispatch(setshoworgScheduleEdit(false))
      }
    },[org_id]);

    const handleEdit = (data:any) => {
      setCurrentData(data)
      dispatch(setshoworgScheduleEdit(true))
      setShowDrawer(true)
    }
    const handleAdd = () => {
      dispatch(setshoworgScheduleEdit(true))
      setShowDrawer(true)
    }
    const cancelAddUpadte = () => {
      dispatch(setshoworgScheduleEdit(false))
      setCurrentData({})
      setShowDrawer(false)
    }


    const handleDelete = (status:boolean) => {
      setShowDeleteModal(status)
    }

    const submitDelete = () => {
      if(currentData?.id){
        deleteOrgSchedule(org_id,{id:currentData?.id});
        setCurrentData({})
        setShowDeleteModal(false)
    }
  }
    
  const sortData = (data: any[]) => {
    return [...data].sort((a, b) => {
      if (sortConfig.key === 'date') {
        const dateA = new Date(a[sortConfig.key]).getTime();
        const dateB = new Date(b[sortConfig.key]).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleSort = (key: string) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };


  const sortedScheduleData = sortData(orgSchedule);

  const scheduleColumns = [
    { 
      key: 'name', 
      label: 'Name',
      width: 'w-[30%]',
      render: (value: string) => (
        <div className="max-w-[250px] overflow-hidden text-ellipsis" title={value}>
          {value}
        </div>
      )
    },
    { 
      key: 'date', 
      label: 'Date',
      width: 'w-[30%]',
      render: (value: string) => (
        <div className="w-48 truncate" title={formatDateWithDay(value)}>
          {formatDateWithDay(value)}
        </div>
      )
    },
    { key: 'type', label: 'Type', width: 'w-[20%]'   },
    {
      key: 'action',
      label: 'Action',
      width: 'w-[20%]',
      render: (row: any) => {
        const isOpen = openMenuId === row.id;
        return (
          <div className="relative flex justify-start">
            <button
              className="p-1 rounded-full text-light-text-secondary dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Actions"
              onClick={e => {
                e.stopPropagation();
                setOpenMenuId(isOpen ? null : row.id);
              }}
            >
              <HiOutlineDotsVertical className="text-xl h-4 w-4" />
            </button>
            {isOpen && (
              <div
                ref={el => (menuRefs.current[row.id] = el)}
                className="absolute w-auto left-4 -top-2 z-[999999] mt-2  rounded-md shadow-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700"
                style={{zIndex: 999999}}
              >
                <button
                  className="w-full px-4 py-2 text-left text-light-text-primary dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 text-sm"
                  onClick={e => {
                    e.stopPropagation();
                    handleEdit(row);  
                    setOpenMenuId(null);
                  }}
                >
                  Edit
                </button>
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-700 text-sm text-red-600"
                  onClick={e => {
                    e.stopPropagation();
                    setCurrentData(row);
                    setShowDeleteModal(true);
                    setOpenMenuId(null);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="flex-1 row bg-light-surface dark:bg-tertiary-dark w-full h-full">
      <div className="p-8  mx-auto w-full divide-y dark:divide-gray-700 h-full flex flex-col">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between md:space-y-0 md:space-x-4 mb-4">
          <div className="flex items-start flex-1 mb-2 space-x-4">
            <div className="flex items-center flex-1  space-x-4">
              <div className="flex flex-col flex-1 gap-1">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">
                  Holidays
                </h4>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter']">
                  Manage company holidays. Project dates computation accounts for holidays.
                </p>
              </div>
            </div>
          </div>
          {!showOrgScheduleEdit && (
            <div className="w-full md:w-auto  flex justify-end orgHoliday" ref={addHolidayWrapperRef}>
              <CustomButton
                onClick={handleAdd}
                size="md"
                leftIcon={<HiPlusCircle className="text-xl" />}
              >
                Add new Holiday
              </CustomButton>
            </div>
          )}
        </div>

        <div className="w-full flex overflow-hidden">
          <div className="mt-8 flex-1">
            <DataTable
              data={sortedScheduleData}
              columns={scheduleColumns}
              sortConfig={sortConfig}
              handleSort={handleSort}
              handleEdit={handleEdit}
              loading={orgScheduleLoading}
              skeletonRows={10}
              tableType="generic"
            />
          </div>
        </div>
      </div>

      <SchedulesModal
        isOpen={showDrawer}
        onClose={cancelAddUpadte}
        currentData={currentData}
      />

      {showHolidaySpotlight && !showOrgScheduleEdit && (
        <OnboardingSpotlight
          key={`orgholidays-${restartToken}`}
          targetRef={addHolidayWrapperRef}
          message="Add organization holidays and schedules to make timelines accurate."
          onDismiss={() => {
            goToNextFrom(OnboardingStepKeyEnum.OrgHolidays);
            completeHoliday();
          }}
          onClose={async () => {
            const next = { ...(onboardSettings || {}), dismissed: true };
            dispatch(setOrganizationOnboardSettings(next));
            await updateOrganization(JWTdata?.org_id, { onboard_settings: next }, true);
          }}
          variant="spotlight"
          stepIndex={Math.max(0, getIndexForStepKey(OnboardingStepKeyEnum.OrgHolidays))}
          totalSteps={globalTotal}
          onBack={() => { /* dismiss-only; previous steps are managed in their own views */ }}
          onSkip={() => { completeHoliday(); }}
        />
      )}

      <ConfirmDeleteModal
          showModal={showDeleteModal} 
          setShowModal={handleDelete} 
          handleConfirm={submitDelete} 
          title="Delete Holiday"
          description={`Are you sure you want to delete "${currentData?.name || 'this holiday'}"? This action cannot be undone.`}
      />
    </div>
  )
}

export default Schedule;